"use client"

import React from "react"
import { Check } from "lucide-react"
import { useIntl } from "react-intl"

import { cn } from "@/lib/utils"
import {
  StepperCard,
  type StepperCardData,
} from "./StepperCard"
import { Badge } from "@/shadcn/components/ui/badge"
import { Button } from "@/shadcn/components/ui/button"
import { Card, CardContent } from "@/shadcn/components/ui/card"
import { Progress } from "@/shadcn/components/ui/progress"

// Subscription (account / plan) page. Figma: S311lGXE6JSyTnNAXHmtR7, frames
// 28187-3499 (default) / 28187-3571 (canceled) / 28187-3643 (success), each with
// a mobile counterpart. One responsive component; all content is prop-driven
// with defaults from the default-state design so it drops into prod unwired.

declare var moment: any;

type ExploreOption = {
  title: string
  description: string
  cta: string
}

type ActiveCoupon = {
  code: string
  discount: string
  expires?: string
}

type AddOnType = {
  addOn: number
  quantity: number
}

type PlanStatus = {
  expired: boolean
  active: boolean
  pastDue: boolean
  suspended: boolean
  canceled: boolean
  paused: boolean
}

type PlanAddOn = {
  name: string
  qty: number
  /** Per-unit benefits granted by this add-on (e.g. socials, feeds, posts). */
  benefits: Record<string, number>
  cost: number
  unitCost: number
}

type PlanData = {
  planLevel: number
  planName: string
  planCode: string
  planTerm: number
  /** Datetime string, e.g. "2026-08-15 16:11:20". */
  nextBilling: string
  status: PlanStatus
  price: number
  addOns: Record<string, PlanAddOn>
  uuid: string
  /** Unix timestamp; 0 when not in a trial. */
  trialEndsAt: number
  coupons: string[]
  /** Unix timestamps; 0 when the plan is not paused. */
  pauseStart: number
  pauseEnd: number
  pauseCycles: number
}

interface PlanDetail {
  planLevel: number;
  label: string;

  /** Base plan price per billing term. */
  price: Record<string, number | number[]>;

  /** Add-on id (e.g. "add_on_social") keyed by add-on slot ("1", "2", ...). */
  addOns: Record<string, string>;

  /** Per-add-on prices, keyed by the same add-on slot as `addOns`. */
  addOnPrices: Record<string, Record<string, number>>;

  /**
   * Quantities granted by each add-on, keyed by add-on slot.
   * Inner keys vary by add-on (e.g. socials/feeds, posts).
   */
  addOnQuantity: Record<string, Record<string, number>>;

  limits: PlanLimits;
}

interface PlanLimits {
  [key: string]: string | number[] | string[];
}

type CalculatedLimit = {
  addOnInputs: number
  addOnOutputs: number
  addOnPosts: number
  addOnPostsPrice: number
  addOnSocialsPrice: number
  baseInputs: number
  baseOutputs: number
  inputs: number
  inputsInUse: number
  outputs: number
  outputsInUse: number
  price: number
}

type SubscriptionPause = {
  start: Date,
  end: Date,
  cycles: number
}

type SubscriptionPageProps = {
  constants: any
  className?: string
  heading?: string
  subheading?: string
  planName: string
  planPrice: string
  plan: PlanData
  planDetails?: PlanDetail
  currentAddOns: {
    add_on_posts_1: number
    add_on_social: number
  }
  postLimit: string | number
  currentLimits: CalculatedLimit
  nextLimits: CalculatedLimit
  onClickSocialAddOns: (qty: number) => void
  onClickXPosts: (qty: number) => void
  onConfirmAddOn: (type: string) => void
  onCancelAddOn: (type: string) => void
  cancelPlan: () => void
  restartPlan: () => void
  savingsPct: number
  savingsTotal: number
  activeCoupons: Array<ActiveCoupon>
  subscriptionPause?: SubscriptionPause
  comparePlans: () => void
  addOnError: string
  changeTerm: (event: any, setRequestParams: () => void) => void
  setRequestParams: () => void
  promoClick: () => void
  resumeSubscription: () => void
}

export default function SubscriptionPage({
  constants,
  className,
  heading = "Your plan",
  subheading = "Manage your subscription and usage",
  planName = "",
  planPrice = "",
  plan,
  planDetails,
  currentAddOns,
  postLimit,
  currentLimits,
  nextLimits,
  onClickSocialAddOns,
  onClickXPosts,
  onConfirmAddOn,
  onCancelAddOn,
  cancelPlan,
  restartPlan,
  savingsPct,
  savingsTotal,
  activeCoupons = [],
  subscriptionPause,
  comparePlans = () => {},
  addOnError = "",
  changeTerm = () => {},
  setRequestParams = () => {},
  promoClick = () => {},
  resumeSubscription = () => {},
}: SubscriptionPageProps) {
  const intl = useIntl()
  let hasUnlimitedProfiles = false

  const usagePct = currentLimits.outputs > 0
      ? Math.min(100, Math.round((currentLimits.outputsInUse / currentLimits.outputs) * 100))
      : 0;

  const termLabel = (plan.planTerm === constants.SUBSCRIPTION_TERM_MONTHLY) ? "/mo" : "/yr";

  if (currentLimits.baseOutputs === -1) {
    hasUnlimitedProfiles = true;
  }

  const stats = [
    { label: 'Feeds & Sites', value: currentLimits.inputs === -1 ? 'Unlimited' : `${currentLimits.inputsInUse} of ${currentLimits.inputs}` },
    { label: 'Posts this month', value: postLimit },
    { label: 'Renewal Date', value: moment(plan.nextBilling).format("MMM D, YYYY")}
  ];

  let socialOutputs: StepperCardData | null = {
      label: 'Social Outputs',
      value: currentAddOns.add_on_social,
      onClick: onClickSocialAddOns,
      onConfirm: () => onConfirmAddOn("add_on_social"),
      state: "default",
      onCancel: () => onCancelAddOn("add_on_social")
    };

  if (planDetails?.addOns[constants.SUBSCRIPTION_ADDON_SOCIALS]) {
    if ((plan.addOns?.add_on_social && plan.addOns?.add_on_social.qty !== currentAddOns.add_on_social) ||
        (!plan.addOns?.add_on_social && currentAddOns.add_on_social) ||
        addOnError
    ) {
      socialOutputs.state = "changed";
      let price = planDetails?.addOnPrices[constants.SUBSCRIPTION_ADDON_SOCIALS][plan.planTerm],
          nextPrice = "";

      if (price) {
        nextPrice = `$${(price * currentAddOns.add_on_social).toFixed(2)}${termLabel}`;
      }
      socialOutputs.price = nextPrice;

      if (addOnError) {
        socialOutputs.errorMessage = addOnError;
        socialOutputs.state = "error";
      }

    }
  } else {
    socialOutputs = null;
  }

  const postsPerAddOn = planDetails?.addOnQuantity[constants.SUBSCRIPTION_ADDON_POSTS_1]?.posts ?? 0;

  let extraXPosts: StepperCardData | null = {
      label: 'Extra X posts / month',
      value: currentAddOns.add_on_posts_1,
      displayValue: intl.formatNumber(currentAddOns.add_on_posts_1 * postsPerAddOn),
      onClick: onClickXPosts,
      onConfirm: () => onConfirmAddOn("add_on_posts_1"),
      state: "default",
      onCancel: () => onCancelAddOn("add_on_posts_1")
    };

  if (planDetails?.addOns[constants.SUBSCRIPTION_ADDON_POSTS_1]) {
    if ((plan.addOns?.add_on_posts_1 && plan.addOns?.add_on_posts_1.qty !== currentAddOns.add_on_posts_1) ||
        (!plan.addOns?.add_on_posts_1 && currentAddOns.add_on_posts_1)) {
      extraXPosts.state = "changed";
      let price = planDetails?.addOnPrices[constants.SUBSCRIPTION_ADDON_POSTS_1][plan.planTerm],
          nextPrice = "";

      if (price) {
        nextPrice = `$${(price * currentAddOns.add_on_posts_1).toFixed(2)}${termLabel}`;
      }
      extraXPosts.price = nextPrice;
    }

  } else {
    extraXPosts = null;
  }

  let notRenewCard: ExploreOption | null = null,
      cancelCard: ExploreOption | null = null,
      pausedCard: ExploreOption | null = null,
      annualNudge: ExploreOption | null = null,
      upgradeBanner: { title: string; cta: string; features: string[], level: number } | null = null;

  if (plan.status?.active) {
    cancelCard = {
      title: "Cancel Plan",
      description: "Cancel your subscription. Your plan stays active until " + moment(plan.nextBilling).format("MMM D, YYYY"),
      cta: "Cancel plan",
    }
  } else if (plan.status?.canceled) {
    notRenewCard = {
      title: "Your plan will not renew",
      description: `Your subscription is set to cancel on ${moment(plan.nextBilling).format("MMM D, YYYY")}. You'll keep full access until that date. You can restart anytime before then to keep your plan active.`,
      cta: "Restart plan",
    }
  }

  if (subscriptionPause?.start) {
    // Figma 29055-2341 — banner sits under the page header; steppers are disabled.
    let title = "Your account is paused",
        description = "Posting is paused, so nothing is publishing to your connected accounts right now. Resume to pick up where you left off.",
        cta = "";

    if (subscriptionPause) {
      let pauseDuration = "months";
      if (subscriptionPause?.cycles === 1) {
        pauseDuration = "month";
      }
      if (moment(subscriptionPause.start).isAfter()) {
        title = "Your account will be paused";
        description = `Your plan will be paused on ${moment(subscriptionPause?.start).format("MMMM D, YYYY")} for ${subscriptionPause?.cycles} ${pauseDuration}`;
      } else {
        description = `Your plan is paused until ${moment(subscriptionPause?.end).format("MMMM D, YYYY")}.`;
        cta = "Reesume Account";
      }

    }

    pausedCard = {
      title,
      description,
      cta
    };

    if (socialOutputs) {
      socialOutputs.state = "disabled"
      socialOutputs.className = "tw:opacity-50"
    }
    if (extraXPosts) {
      extraXPosts.state = "disabled"
      extraXPosts.className = "tw:opacity-50"
    }
  }

  // Paused: hide Explore Options (annual nudge + upgrade) — not in Figma 29055-2341.
  if (!plan.status?.paused) {
  switch (plan.planLevel) {
    case constants.PUB_LEVEL_2:
    case constants.PUB_LEVEL_3:
    case constants.PUB_LEVEL_4:
    case constants.PUB_LEVEL_6:
    case constants.PUB_LEVEL_7:
      upgradeBanner = {
        title: "Get more with dlvr.it Plus",
        cta: "Upgrade to Plus",
        features: ["Post recycler", "Bulk import", "Smart hashtags"],
        level: constants.PUB_LEVEL_9
      }
      break;

    case constants.PUB_LEVEL_8:
      upgradeBanner = {
        title: "Powerful Tools at Agency Scale",
        cta: "Upgrade to Agency+",
        features: ["Everqueue", "Categories", "Smart hashtags"],
        level: constants.PUB_LEVEL_10
      }
      break;

    case constants.PUB_LEVEL_9:
      upgradeBanner = {
        title: "Scale your team with Agency",
        cta: "Upgrade to Agency",
        features: ["Team members", "Clients", "250+ outputs"],
        level: constants.PUB_LEVEL_8
      }
      break;

    case constants.PUB_LEVEL_15:
    case constants.PUB_LEVEL_16:
      upgradeBanner = {
        title: "Boost your reach and grow your audience",
        cta: "Upgrade to Pro",
        features: ["10+ Outputs", "Real time updates", "Flexible"],
        level: constants.PUB_LEVEL_6
      }
      break;

    default:
      break;

  }

  if (plan.planTerm === constants.SUBSCRIPTION_TERM_MONTHLY) {
    let annualPrice = planDetails?.price[constants.SUBSCRIPTION_TERM_ANNUAL],
        annualPriceLabel = "";
    if (typeof annualPrice === "number") {
      annualPriceLabel = annualPrice.toFixed(2);
    }

    annualNudge = {
      title: `Save ${savingsPct}% with annual billing`,
      description: `$${annualPriceLabel}/yr · save $${savingsTotal}`,
      cta: "Switch to annual",
    }
  }
  } // end !paused explore options

  return (
    <div
      className={cn(
        "tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:font-sans tw:flex-col tw:gap-4 tw:text-foreground tw:p-4 tw:md:p-6",
        className
      )}
    >
      <header className="tw:flex tw:flex-col tw:gap-1">
        <h1 className="tw:text-2xl tw:font-semibold">{heading}</h1>
        <p className="tw:text-sm tw:text-muted-foreground">{subheading}</p>
      </header>

      {pausedCard ? (
        <Card className="tw:flex tw:flex-col tw:gap-3 tw:border-destructive/40 tw:ring-destructive/40 tw:py-0 tw:p-4 tw:md:flex-row tw:md:items-start tw:md:justify-between tw:md:gap-4">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <p className="tw:text-base tw:font-medium tw:text-destructive">
              {pausedCard.title}
            </p>
            <p className="tw:text-sm tw:text-destructive">
              {pausedCard.description}
            </p>
          </div>
          {pausedCard.cta ? (
            <Button
              variant="outline"
              size="sm"
              className="tw:w-full tw:shrink-0 tw:text-xs tw:md:w-auto"
              onClick={resumeSubscription}
            >
              {pausedCard.cta}
            </Button>
          ) : null}

        </Card>
      ) : null}

      {/* Plan Summary — white content zone + full-bleed muted stepper strip */}
      <Card className="tw:gap-0 tw:rounded-lg tw:py-0">
        <CardContent className="tw:flex tw:flex-col tw:gap-6 tw:p-4 tw:md:p-6">
          {/* Plan name + price badge, with a right-aligned "Compare plans" link.
              Mobile: name & link share row 1, badge drops to row 2 (Figma
              28187-3535). Desktop: name · badge · link-far-right (28187-3499).
              The link is rendered per-breakpoint to get that reflow cleanly. */}
          <div className="tw:flex tw:flex-col tw:gap-2 tw:md:flex-row tw:md:items-center tw:md:gap-2">
            <div className="tw:flex tw:items-center tw:gap-2">
              <span className="tw:text-xl tw:font-semibold">{planName}</span>
              <button
                type="button"
                className="tw:ml-auto tw:text-xs tw:text-muted-foreground tw:hover:underline tw:md:hidden"
                onClick={comparePlans}
              >
                Compare plans
              </button>
            </div>
            <Badge variant="secondary">{planPrice}{termLabel}</Badge>
            <button
              type="button"
              className="tw:hidden tw:text-xs tw:text-muted-foreground tw:hover:underline tw:md:ml-auto tw:md:block"
              onClick={comparePlans}
            >
              Compare plans
            </button>
          </div>

          <div className="tw:flex tw:flex-col tw:gap-2">
            {hasUnlimitedProfiles ? (
              <p className="tw:text-base tw:font-semibold">Unlimited social profiles</p>
            ) : (
              <>
                <p className="tw:text-base tw:font-semibold">
                  {currentLimits.outputsInUse} of {currentLimits.outputs} Social Profiles in use
                </p>
                <Progress value={usagePct} className="tw:h-1" />
              </>
            )}
          </div>

          <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3">
            {stats.map((stat) => (
              <div key={stat.label} className="tw:flex tw:flex-col tw:gap-2">
                <span className="tw:text-sm tw:text-muted-foreground">
                  {stat.label}
                </span>
                <span className="tw:text-base tw:font-semibold">{stat.value}</span>
              </div>
            ))}
          </div>

          {/* Applied promo code(s). Figma 28187-3499 (node 29070:1662). */}
          {activeCoupons.length > 0 ? (
            <div className="tw:flex tw:flex-col tw:gap-1">
              {activeCoupons.map((coupon) => (
                <p key={coupon.code} className="tw:text-xs tw:text-primary">
                  {coupon.discount} off promo code
                  {coupon.expires ? ` · expires ${coupon.expires}` : ""}
                </p>
              ))}
            </div>
          ) : null}
        </CardContent>

        {(socialOutputs || extraXPosts) ? (
          <div
            className={cn(
              "tw:grid tw:grid-cols-1 tw:gap-3 tw:bg-muted tw:p-4 tw:md:px-6 tw:md:py-5",
              (socialOutputs && extraXPosts) && "tw:md:grid-cols-2"
            )}
          >
            {socialOutputs ? (
              <StepperCard
                {...socialOutputs} />
             ) : null}
            {extraXPosts ? (
              <StepperCard
                {...extraXPosts} />
              ) : null}
          </div>
        ) : null}
      </Card>

      {/* Explore Options — annual nudge + upgrade banner inside one wrapper */}
      {(annualNudge || upgradeBanner) ? (
      <Card className="tw:gap-3 tw:rounded-lg tw:p-4 tw:md:p-6">
        {annualNudge ? (
          <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:bg-secondary tw:p-4 tw:md:flex-row tw:md:items-center tw:md:justify-between">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <p className="tw:text-sm tw:font-semibold tw:text-secondary-foreground">
                {annualNudge.title}
              </p>
              <p className="tw:text-sm tw:text-muted-foreground">
                {annualNudge.description}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="tw:w-full tw:shrink-0 tw:text-xs tw:md:w-auto"
              data-term={constants.SUBSCRIPTION_TERM_ANNUAL}
              onClick={(e) => {
                changeTerm(e, setRequestParams);
              }}
            >
              {annualNudge.cta}
            </Button>
          </div>
          ) : null }

        {upgradeBanner ? (
          <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:bg-gradient-to-r tw:from-primary tw:to-primary/70 tw:p-4">
            <div className="tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-center tw:md:justify-between">
              <p className="tw:text-sm tw:font-semibold tw:text-primary-foreground">
                {upgradeBanner.title}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="tw:w-full tw:shrink-0 tw:text-xs tw:md:w-auto"
                data-plan={upgradeBanner.level}
                onClick={promoClick}
              >
                {upgradeBanner.cta}
              </Button>
            </div>
            <div className="tw:flex tw:flex-wrap tw:gap-x-4 tw:gap-y-1">
              {upgradeBanner.features.map((feature) => (
                <span
                  key={feature}
                  className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-primary-foreground"
                >
                  <Check className="tw:size-3.5" />
                  {feature}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </Card>
      ) : null}

      {/* State-dependent bottom card */}
      {plan.status?.canceled && notRenewCard ? (
        <Card className="tw:mt-4 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border-destructive/30 tw:ring-destructive/20 tw:py-0 tw:p-4 tw:md:flex-row tw:md:items-start tw:md:justify-between tw:md:gap-4 tw:md:p-6">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <p className="tw:text-sm tw:font-semibold tw:text-destructive">
              {notRenewCard.title}
            </p>
            <p className="tw:text-sm tw:text-destructive/80">
              {notRenewCard.description}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="tw:w-full tw:shrink-0 tw:text-xs tw:md:w-auto"
            onClick={restartPlan}
          >
            {notRenewCard.cta}
          </Button>
        </Card>
      ) : null}
      {plan.status?.active && cancelCard ? (
        <Card className="tw:mt-4 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:py-0 tw:p-4 tw:md:flex-row tw:md:items-start tw:md:justify-between tw:md:gap-4 tw:md:p-6">
          <div className="tw:flex tw:flex-col tw:gap-1">
            <p className="tw:text-sm tw:font-semibold">{cancelCard.title}</p>
            <p className="tw:text-sm tw:text-muted-foreground">
              {cancelCard.description}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="tw:w-full tw:shrink-0 tw:text-xs tw:md:w-auto"
            onClick={cancelPlan}
          >
            {cancelCard.cta}
          </Button>
        </Card>
      ) : null}
    </div>
  )
}
